/* ==========================================================================
   AndroonBazar — global.js
   Vanilla JS only. No framework, no jQuery, no animation library.
   Every component is a custom element so it re-initialises correctly when the
   Theme Editor re-renders a section.
   ========================================================================== */

(function () {
  'use strict';

  /* --- Utilities --------------------------------------------------------- */

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  const AB = (window.AndroonBazar = window.AndroonBazar || {});

  AB.focusable = (root) =>
    Array.from(root.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );

  AB.trapFocus = function (container, event) {
    if (event.key !== 'Tab') return;
    const items = AB.focusable(container);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  AB.lockScroll = function (lock) {
    document.body.classList.toggle('is-locked', lock);
  };

  AB.debounce = function (fn, wait) {
    let timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, arguments), wait);
    };
  };

  AB.formatMoney = function (cents) {
    // Uses the shop's own money format, injected in theme.liquid.
    const format = (window.themeStrings && window.themeStrings.moneyFormat) || '';
    const value = (cents / 100).toLocaleString('en-PK', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return format ? format.replace(/\{\{\s*amount_no_decimals\s*\}\}|\{\{\s*amount\s*\}\}/, value) : value;
  };

  /* --- Drawer ------------------------------------------------------------
     <ab-drawer id="search-drawer" data-position="right"> … </ab-drawer>
     Opened by any [data-drawer-open="search-drawer"] button.
     ---------------------------------------------------------------------- */

  class ABDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-drawer-panel]');
      this.opener = null;
      this.setAttribute('hidden', '');

      this.onKeydown = this.onKeydown.bind(this);

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') || e.target === this) this.close();
      });
    }

    open(opener) {
      this.opener = opener || null;
      this.removeAttribute('hidden');
      // Next frame so the transition runs.
      requestAnimationFrame(() => this.classList.add('is-open'));
      AB.lockScroll(true);
      document.addEventListener('keydown', this.onKeydown);

      const target = this.querySelector('[data-drawer-focus]') || AB.focusable(this)[0];
      if (target) setTimeout(() => target.focus(), 60);

      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (this.hasAttribute('hidden')) return;
      this.classList.remove('is-open');
      AB.lockScroll(false);
      document.removeEventListener('keydown', this.onKeydown);

      const done = () => this.setAttribute('hidden', '');
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) done();
      else setTimeout(done, 320);

      if (this.opener) this.opener.focus();
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        return;
      }
      AB.trapFocus(this, e);
    }
  }

  if (!customElements.get('ab-drawer')) customElements.define('ab-drawer', ABDrawer);

  document.addEventListener('click', function (e) {
    const opener = e.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.getAttribute('data-drawer-open'));
    if (!drawer) return;
    e.preventDefault();
    drawer.open(opener);
  });

  /* --- Sticky header -----------------------------------------------------
     Adds .is-stuck once past the announcement bar, and .is-solid when a
     transparent header needs a background.
     ---------------------------------------------------------------------- */

  class ABHeader extends HTMLElement {
    connectedCallback() {
      this.sticky = this.dataset.sticky === 'true';
      if (!this.sticky) return;

      this.threshold = 24;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      const past = window.scrollY > this.threshold;
      this.classList.toggle('is-stuck', past);
    }
  }

  if (!customElements.get('ab-header')) customElements.define('ab-header', ABHeader);

  /* --- Mega menu ---------------------------------------------------------
     Hover on desktop (pointer: fine), click/keyboard everywhere.
     ---------------------------------------------------------------------- */

  class ABMenuItem extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-menu-trigger]');
      this.panel = this.querySelector('[data-menu-panel]');
      if (!this.trigger || !this.panel) return;

      this.hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      this.trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle(!this.isOpen);
      });

      if (this.hoverCapable) {
        this.addEventListener('mouseenter', () => this.toggle(true));
        this.addEventListener('mouseleave', () => this.toggle(false));
      }

      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.toggle(false);
      });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.toggle(false);
          this.trigger.focus();
        }
      });
    }

    get isOpen() {
      return this.trigger.getAttribute('aria-expanded') === 'true';
    }

    toggle(open) {
      this.trigger.setAttribute('aria-expanded', String(open));
      this.classList.toggle('is-open', open);
    }
  }

  if (!customElements.get('ab-menu-item')) customElements.define('ab-menu-item', ABMenuItem);

  /* --- Mobile nav drill-down --------------------------------------------- */

  class ABMobileNav extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const push = e.target.closest('[data-nav-push]');
        const back = e.target.closest('[data-nav-back]');

        if (push) {
          e.preventDefault();
          const level = push.nextElementSibling;
          if (level) {
            level.classList.add('is-active');
            const first = AB.focusable(level)[0];
            if (first) setTimeout(() => first.focus(), 60);
          }
        }

        if (back) {
          e.preventDefault();
          const level = back.closest('[data-nav-level]');
          if (level) {
            level.classList.remove('is-active');
            const trigger = level.previousElementSibling;
            if (trigger) trigger.focus();
          }
        }
      });
    }
  }

  if (!customElements.get('ab-mobile-nav')) customElements.define('ab-mobile-nav', ABMobileNav);

  /* --- Scroll reveal ------------------------------------------------------ */

  function initReveals(root) {
    const motion = document.documentElement.dataset.motion;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const targets = (root || document).querySelectorAll('[data-reveal]:not(.is-revealed)');

    if (motion === 'none' || reduced || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    targets.forEach((el) => observer.observe(el));
  }

  AB.initReveals = initReveals;

  /* --- Boot --------------------------------------------------------------- */

  document.addEventListener('DOMContentLoaded', function () {
    initReveals();
  });

  // Theme Editor: re-run section-level init after a section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    initReveals(e.target);
  });
})();
